/**
 * The search box and paged option list a lazily-loaded filter shows, wherever
 * it is shown.
 *
 * The desktop chip renders it inside its popover and the mobile filter sheet
 * renders it inside its drill-down panel, so a reader narrowing by site group
 * on a phone gets the same server-side search and the same paging as on a
 * desktop rather than a truncated first page.
 *
 * Its search box is the toolbar's, so narrowing a chip's vocabulary looks like
 * narrowing a grid. A bare TextField here inherited the theme's filled variant
 * and, having no label, reserved the space a floating one would have used as
 * dead padding above the text.
 *
 * A row is a plain label unless the caller says otherwise. A picker whose
 * server searches more than the label — a site group's description, say — hands
 * in its own row renderer, because a match the reader cannot see the reason for
 * reads as a wrong result.
 */
import Box from '@mui/material/Box';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Typography from '@mui/material/Typography';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ToolbarSearchInput } from '@sophocles/data-grids/components/toolbar';

// How close to the end of the list counts as having reached the end.
const SCROLL_EPSILON_PX = 48;

/**
 * @param {object} props
 * @param {ReturnType<import('./useAsyncFilterOptions').default>} props.state - the paging state.
 * @param {?string} props.value - the chosen option's value, or null for unset.
 * @param {(value: ?string) => void} props.onSelect
 * @param {string} props.allLabel - what the leading "no narrowing" row reads.
 * @param {?string} [props.searchPlaceholder]
 * @param {?(option: object, searchText: string) => React.ReactNode} [props.renderOption] -
 *   the body of one option row; omitted renders the option's label alone.
 * @param {number} [props.maxHeight] - list height before it scrolls.
 * @param {boolean} [props.autoFocus] - focus the search box on mount.
 */
export default function AsyncFilterOptionList({
  state,
  value,
  onSelect,
  allLabel,
  searchPlaceholder = null,
  renderOption = null,
  maxHeight = 320,
  autoFocus = true,
}) {
  const { t } = useTranslation();
  const { draft, setDraft, options, loading, hasMore, remaining, loadMore } = state;

  const handleScroll = useCallback(
    event => {
      if (loading || !hasMore) return;
      const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
      if (scrollHeight - scrollTop - clientHeight <= SCROLL_EPSILON_PX) loadMore();
    },
    [loading, hasMore, loadMore],
  );

  return (
    <>
      {/* The search field sizes itself with `flex: 1`, so its wrapper is a row. */}
      <Box sx={{ px: 2, pb: 1, display: 'flex' }}>
        <ToolbarSearchInput
          autoFocus={autoFocus}
          value={draft}
          onChange={setDraft}
          placeholder={searchPlaceholder ?? t('general.search', 'Search')}
          inputProps={{ 'aria-label': searchPlaceholder ?? t('general.search', 'Search') }}
        />
      </Box>
      <MenuList dense sx={{ maxHeight, overflowY: 'auto', py: 0.5 }} onScroll={handleScroll}>
        <MenuItem selected={value == null} onClick={() => onSelect(null)}>
          <ListItemText primary={allLabel} slotProps={{ primary: { variant: 'body2' } }} />
        </MenuItem>
        {options.map(option => (
          <MenuItem key={option.value} selected={option.value === value} onClick={() => onSelect(option.value)}>
            {renderOption ? (
              renderOption(option, draft)
            ) : (
              <ListItemText primary={option.label} slotProps={{ primary: { variant: 'body2' } }} />
            )}
          </MenuItem>
        ))}
        {loading && (
          <Typography variant="caption" sx={{ display: 'block', px: 2, py: 1, color: 'text.secondary' }}>
            {t('general.loading', 'Loading...')}
          </Typography>
        )}
        {!loading && hasMore && (
          <Typography variant="caption" sx={{ display: 'block', px: 2, py: 1, color: 'text.secondary' }}>
            {t('filters.moreResults', { count: remaining, defaultValue: '{{count}} more…' })}
          </Typography>
        )}
        {!loading && options.length === 0 && (
          <Typography variant="caption" sx={{ display: 'block', px: 2, py: 1, color: 'text.secondary' }}>
            {t('filters.noMatches', 'No matches')}
          </Typography>
        )}
      </MenuList>
    </>
  );
}
